import { tr } from './i18n';
import { ago, duration } from './ui';

export type Tone = 'ok' | 'warn' | 'bad' | 'info' | 'neutral';

/** What the portal knows about a computer's state (from the computer list, detail or dashboard). */
export interface ComputerLike {
  status: string;
  protection?: { online: boolean; protected: boolean; reason: string };
  reason?: string;
  online?: boolean;
  last_success_at?: string | null;
  last_seen_at?: string | null;
  last_job_state?: string | null;
}

export interface ComputerStatus {
  tone: Tone;
  /** One short sentence: "Backed up 2 hours ago". */
  label: string;
  /** What to do about it, when something needs attention. */
  hint?: string;
  /** "Online now" / "Last seen 3 hours ago". */
  seen: string;
  needsAttention: boolean;
}

/** The single, plain-language description of a computer's backup state used on every screen. */
export function computerStatus(d: ComputerLike, backingUp = false): ComputerStatus {
  const reason = d.protection?.reason ?? d.reason ?? 'ok';
  const online = d.protection?.online ?? d.online ?? false;
  const seen = online ? tr('Online now') : d.last_seen_at ? tr('Last seen {time}', { time: ago(d.last_seen_at) }) : tr('Not connected yet');
  const offHint = tr('Turn the computer on and connect it to the internet. It backs up automatically.');
  const base = { seen, needsAttention: false };
  if (d.status === 'retired') return { ...base, tone: 'neutral', label: tr('Removed'), hint: tr('Its backups are kept and can still be restored.') };
  if (d.status === 'quarantined')
    return { ...base, tone: 'bad', label: tr('Blocked'), hint: tr('Blocked because it was reported lost or stolen. Its backups can still be restored to another computer.') };
  if (d.status === 'pending') return { ...base, tone: 'neutral', label: tr('Setting up…'), hint: tr('The computer is connected and getting ready. This usually takes a few minutes.') };
  if (backingUp || d.last_job_state === 'running') return { ...base, tone: 'info', label: tr('Backing up now…') };
  switch (reason) {
    case 'ok':
      return { ...base, tone: 'ok', label: tr('Backed up {time}', { time: ago(d.last_success_at) }) };
    case 'paused':
      return { ...base, tone: 'warn', label: tr('Backups paused'), hint: tr("Backups are paused in this computer's backup plan. Resume them on the plan's page."), needsAttention: true };
    case 'no_policy':
      return { ...base, tone: 'bad', label: tr('No backup plan'), hint: tr('Choose a backup plan for this computer.'), needsAttention: true };
    case 'no_recovery_point':
      return { ...base, tone: 'warn', label: tr('Waiting for its first backup'), hint: online ? tr('The first backup can take a while. Leave the computer on.') : offHint, needsAttention: true };
    case 'stale': {
      const since = d.last_success_at ? duration(Date.now() - new Date(d.last_success_at).getTime()) : '';
      return {
        ...base,
        tone: 'bad',
        label: tr('No backup for {time}', { time: since }),
        hint: online ? tr("The computer is on, but backups aren't finishing. Try “Back up now” or look at its backup history.") : offHint,
        needsAttention: true,
      };
    }
    default:
      return { ...base, tone: 'warn', label: tr('Needs attention'), needsAttention: true };
  }
}

/**
 * The same, for a phone. A phone has no backup plan anyone chose and nothing to "turn on": files are
 * saved as they arrive in the app, so the words are about files and about opening the app.
 */
export function phoneStatus(d: ComputerLike): ComputerStatus {
  const reason = d.protection?.reason ?? d.reason ?? 'ok';
  const online = d.protection?.online ?? d.online ?? false;
  const seen = online ? tr('Online now') : d.last_seen_at ? tr('Last seen {time}', { time: ago(d.last_seen_at) }) : tr('Not connected yet');
  const offHint = tr('Open ZeroGap on the phone with an internet connection. Files are saved by themselves.');
  const base = { seen, needsAttention: false };
  if (d.status === 'retired') return { ...base, tone: 'neutral', label: tr('Removed'), hint: tr('Its files are kept and can still be got back.') };
  if (d.status === 'quarantined') return { ...base, tone: 'bad', label: tr('Blocked'), hint: tr('Blocked because it was reported lost or stolen. Its files can still be got back.') };
  if (d.status === 'pending') return { ...base, tone: 'neutral', label: tr('Setting up…'), hint: tr('The phone is connected and getting ready. This takes a minute.') };
  if (d.last_job_state === 'running') return { ...base, tone: 'info', label: tr('Saving files now…') };
  switch (reason) {
    case 'ok':
      return { ...base, tone: 'ok', label: tr('Files saved {time}', { time: ago(d.last_success_at) }) };
    case 'no_recovery_point':
      // Online and empty is simply a phone nobody has added anything to yet; the page says how it works.
      return { ...base, tone: 'warn', label: tr('Waiting for its first files'), ...(online ? {} : { hint: offHint }), needsAttention: !online };
    case 'stale': {
      const since = d.last_success_at ? duration(Date.now() - new Date(d.last_success_at).getTime()) : '';
      return { ...base, tone: 'bad', label: tr('Not saved for {time}', { time: since }), hint: offHint, needsAttention: true };
    }
    default:
      return { ...base, tone: 'warn', label: tr('Needs attention'), hint: offHint, needsAttention: true };
  }
}
